import { playButtonField } from '../_shared/videoPlayButton.js';
import { textColorFields } from '../_shared/textColor.js';
import { headingHighlightFields } from '../_shared/headingHighlight.js';
import { universalFields, mediaRadiusField } from '../_shared/universal.js';
import { tabVideoFields } from '../_shared/tabVideo.js';
/**
 * content-tabs — a FROM-SCRATCH, faithful rebuild of Shrine Pro's "Content tabs" section
 * (shrine-pro-ref/sections/content-tabs.liquid). A highlighted heading over a row of TAB BUTTONS in a
 * bordered ("outlined") / drop-shadow container, with a sliding "moving" active-pill (or an "opacity"
 * cross-fade), and one content panel per tab (heading + text + optional link button + optional image/video
 * media with a 2-column layout). The buttons carry their OWN colour scheme (default accent-1), so the pill /
 * border / inactive labels paint in the accent and the active label flips to the contrast colour — exactly
 * like Shrine. The tab switch + pill animation is a React-free client island (contentTabsInit).
 *
 * Ported faithfully: heading + 2 highlight engines (shared `headingHighlightFields`), tab-button style
 * (outlined/shadow) + colour scheme (+custom) + corner radius + moving/opacity animation, desktop/mobile
 * button font sizes + full-width expand, desktop header layout, and per-tab blocks (button label/icon,
 * heading + highlight colour + size, text, link button, image/video media, desktop/mobile media placement +
 * text alignment). DEFERRED (documented, Shopify buy-box territory): per-tab Add-to-Cart button + product
 * picker (`atc_*`, `product`) and the video PLAY-button chrome (`btn_color_scheme`, `btn_animation`) —
 * uploaded video renders muted-autoplay like an image, matching our image-with-text video model.
 */
const SCHEME_6 = [
    { value: 'accent-1', label: 'Accent 1' },
    { value: 'accent-2', label: 'Accent 2' },
    { value: 'background-1', label: 'Background 1' },
    { value: 'background-2', label: 'Background 2' },
    { value: 'inverse', label: 'Inverse' },
    { value: 'custom', label: 'Custom' },
];
const HEADING_SIZE = [
    { value: 'h3', label: 'Extra small' },
    { value: 'h2', label: 'Small' },
    { value: 'h1', label: 'Medium' },
    { value: 'h0', label: 'Large' },
];
/** The repeatable "Tab" block — one tab button + its content panel (Shrine `tab` block). */
const tabBlockFields = [
    // ── Tab button ──
    {
        // LE NOM DE L'ONGLET, ET L'IA DOIT POUVOIR L'ÉCRIRE (0059, 26/08). Deux gardes le lui
        // interdisaient, et la section sortait inutilisable de chaque page composée :
        //  - `defaultKey` range ce champ avec les libellés d'interface du dictionnaire (0050), donc
        //    `collectTextSlots` ne le relevait pas : le modèle ne voyait QUE le paragraphe du panneau,
        //    jamais le sujet de l'onglet. Le libellé d'un onglet n'est pas « Ajouter au panier » : c'est
        //    du CONTENU («Composition», «Entretien»), d'où `aiWritable: true` — même cas que les titres
        //    de colonnes de 0059 ;
        //  - « Tab BUTTON label » le classait ensuite en étiquette de bouton d'achat, que le composeur
        //    efface partout hors fiche d'achat (PageAssembler.deadButtons). Le champ dit maintenant ce
        //    qu'il est — un onglet se dessine comme un bouton, il n'en est pas un.
        // Sans étiquette, le composant retombe sur « Tab 1 », « Tab 2 »… en anglais, sur une page
        // française : c'est exactement ce que la page composée du 26/08 affichait.
        id: 'btnLabel',
        type: 'text',
        label: 'Tab label',
        group: 'Tab button',
        default: 'Tab button',
        defaultKey: 'section.tab_button',
        aiWritable: true,
        maxLength: 60,
    },
    {
        id: 'btnIcon',
        type: 'icon-name',
        label: 'Icon',
        group: 'Tab button',
        info: 'A Google Material Symbols name (e.g. check_circle). Optional.',
    },
    {
        id: 'filledBtnIcon',
        type: 'checkbox',
        label: 'Filled icon',
        group: 'Tab button',
        default: false,
    },
    {
        id: 'btnCustomIcon',
        type: 'image',
        label: 'Custom icon (overrides the icon)',
        group: 'Tab button',
    },
    // ── Content ──
    {
        id: 'title',
        type: 'text',
        label: 'Heading',
        group: 'Content',
        default: 'Tab heading',
        defaultKey: 'section.tab_heading',
        maxLength: 120,
        info: 'Bold words with **double asterisks** to highlight them with the colour below.',
    },
    {
        id: 'titleHighlightColor',
        type: 'color',
        label: 'Heading highlight color',
        group: 'Content',
        default: { ref: 'theme', token: 'accent' },
    },
    {
        id: 'headingSize',
        type: 'select',
        label: 'Heading size',
        group: 'Content',
        default: 'h1',
        options: HEADING_SIZE,
    },
    {
        id: 'text',
        type: 'richtext',
        label: 'Text',
        group: 'Content',
        default: {
            format: 'html',
            html: '<p>Use this section to display different benefits or products, sorted by clickable tabs.</p>',
        },
    },
    { id: 'buttonLabel', type: 'text', label: 'Button label', group: 'Content', maxLength: 40 },
    { id: 'link', type: 'link', label: 'Button link', group: 'Content' },
    {
        id: 'buttonStyleSecondary',
        type: 'checkbox',
        label: 'Secondary button style',
        group: 'Content',
        default: false,
    },
    // ── Media ──
    { id: 'image', type: 'image', label: 'Image', group: 'Media' },
    {
        id: 'video',
        type: 'video',
        label: 'Video (replaces the image; plays muted, looping)',
        group: 'Media',
    },
    // Le disque lecture/pause de CET onglet (20/09/2026, fondatrice : « add this config to all components
    // that has video in them »). Éteint par défaut. Réglage de BLOC et non de section, contrairement à la
    // paire lecture-auto/son : un onglet peut porter une démo qu'on veut pouvoir arrêter à côté d'un
    // onglet dont le clip n'est qu'un décor.
    playButtonField({
        defaultOn: false,
        group: 'Media',
        visibleWhen: { field: 'video', empty: false },
    }),
    // ── Layout ──
    {
        id: 'desktopMediaWidth',
        type: 'select',
        label: 'Desktop media width',
        group: 'Layout',
        default: 'half',
        options: [
            { value: 'half', label: 'Half of the tab' },
            { value: 'full', label: 'Full width of the tab' },
        ],
    },
    {
        id: 'desktopMediaPosition',
        type: 'select',
        label: 'Desktop media placement',
        group: 'Layout',
        default: 'media_first',
        options: [
            { value: 'media_first', label: 'Media first' },
            { value: 'media_second', label: 'Media second' },
        ],
    },
    {
        id: 'desktopTextAlignment',
        type: 'select',
        label: 'Desktop text alignment',
        group: 'Layout',
        default: 'center',
        options: [
            { value: 'left', label: 'Left' },
            { value: 'center', label: 'Center' },
            { value: 'right', label: 'Right' },
        ],
    },
    {
        id: 'mobileMediaPosition',
        type: 'select',
        label: 'Mobile media placement',
        group: 'Layout',
        default: 'media_first',
        options: [
            { value: 'media_first', label: 'Media first' },
            { value: 'media_second', label: 'Media second' },
        ],
    },
    {
        id: 'mobileTextAlignment',
        type: 'select',
        label: 'Mobile text alignment',
        group: 'Layout',
        default: 'center',
        options: [
            { value: 'left', label: 'Left' },
            { value: 'center', label: 'Center' },
            { value: 'right', label: 'Right' },
        ],
    },
];
export const contentTabsSchema = {
    type: 'content-tabs',
    name: 'Content tabs',
    version: 1,
    templates: ['index', 'collection', 'page', 'product', 'thank-you'],
    fields: [
        // ── General (heading + 2 highlight engines) ──
        {
            // Le titre de la section est du CONTENU, pas un libellé d'interface : sans `aiWritable`, le
            // dictionnaire (0050) l'excluait du relevé et la page composée titrait la section
            // « Onglets de contenu » — le nom du composant, affiché en grand au milieu de l'argumentaire.
            id: 'heading',
            type: 'text',
            label: 'Heading',
            group: 'General',
            default: 'Content tabs',
            defaultKey: 'section.content_tabs',
            aiWritable: true,
            maxLength: 120,
        },
        {
            id: 'headingSize',
            type: 'select',
            label: 'Heading size',
            group: 'General',
            default: 'h1',
            options: HEADING_SIZE,
        },
        ...headingHighlightFields(1),
        ...headingHighlightFields(2),
        // ── Tabs (the button-bar chrome) ──
        {
            id: 'buttonsColorScheme',
            type: 'select',
            label: 'Tab buttons color',
            group: 'Tabs',
            default: 'accent-1',
            options: SCHEME_6,
        },
        {
            id: 'customTabsColor',
            type: 'color',
            label: 'Custom tab buttons & border color',
            group: 'Tabs',
            default: { ref: 'custom', value: '#000000' },
            visibleWhen: { field: 'buttonsColorScheme', equals: 'custom' },
        },
        {
            id: 'customTabsColorLabel',
            type: 'color',
            label: 'Custom active button label color',
            group: 'Tabs',
            default: { ref: 'custom', value: '#FFFFFF' },
            visibleWhen: { field: 'buttonsColorScheme', equals: 'custom' },
        },
        {
            id: 'buttonBorderRadius',
            type: 'range',
            label: 'Buttons corner radius',
            group: 'Tabs',
            min: 0,
            max: 40,
            step: 2,
            unit: 'px',
            default: 6,
        },
        {
            id: 'buttonsStyle',
            type: 'select',
            label: 'Buttons container style',
            group: 'Tabs',
            default: 'outlined',
            options: [
                { value: 'outlined', label: 'Outlined' },
                { value: 'shadow', label: 'Drop shadow' },
            ],
        },
        {
            id: 'activeButtonAnimation',
            type: 'select',
            label: 'Active button animation',
            group: 'Tabs',
            default: 'moving',
            info: 'Choose "Moving" only if all your buttons fit on one row.',
            options: [
                { value: 'opacity', label: 'Opacity' },
                { value: 'moving', label: 'Moving' },
            ],
        },
        // ── Desktop ──
        {
            id: 'headerLayout',
            type: 'select',
            label: 'Desktop header layout',
            group: 'Desktop',
            default: 'horizontal',
            options: [
                { value: 'vertical', label: 'Vertical' },
                { value: 'horizontal', label: 'Horizontal' },
            ],
        },
        {
            id: 'buttonsDesktopFontSize',
            type: 'range',
            label: 'Buttons font size',
            group: 'Desktop',
            min: 14,
            max: 30,
            step: 1,
            unit: 'px',
            default: 15,
        },
        {
            id: 'desktopFullSizeButtons',
            type: 'checkbox',
            label: 'Expand buttons to full width',
            group: 'Desktop',
            default: false,
        },
        // ── Mobile ──
        {
            id: 'buttonsMobileFontSize',
            type: 'range',
            label: 'Buttons font size',
            group: 'Mobile',
            min: 8,
            max: 25,
            step: 1,
            unit: 'px',
            default: 13,
        },
        {
            id: 'mobileFullSizeButtons',
            type: 'checkbox',
            label: 'Expand buttons to full width',
            group: 'Mobile',
            default: true,
        },
        // ── Media (how the tabs' clips play) ──
        ...tabVideoFields,
        // ── Universal band (color scheme / width / padding) ──
        mediaRadiusField,
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        ...universalFields,
    ],
    blocks: [{ type: 'tab', name: 'Tab', fields: tabBlockFields }],
    maxBlocks: 12,
};
const tab = (btnLabel, title, textHtml) => ({
    type: 'tab',
    settings: {
        btnLabel,
        btnIcon: '',
        title,
        headingSize: 'h1',
        text: { format: 'html', html: textHtml },
        desktopTextAlignment: 'center',
        mobileTextAlignment: 'center',
    },
});
export const contentTabsPresets = [
    {
        name: 'Content tabs',
        settings: {
            heading: '@t:section.content_tabs_heading',
            headingSize: 'h1',
            // Engine 1 colours the **bold** word (e.g. "benefits") in the theme accent — matches the reference.
            headingHighlight1: 'solid-color',
            buttonsColorScheme: 'accent-1',
            buttonBorderRadius: 6,
            buttonsStyle: 'outlined',
            activeButtonAnimation: 'moving',
            headerLayout: 'horizontal',
            buttonsDesktopFontSize: 15,
            buttonsMobileFontSize: 13,
            desktopFullSizeButtons: false,
            mobileFullSizeButtons: true,
            colorScheme: 'light',
            paddingTop: 40,
            paddingBottom: 40,
            width: 'standard',
        },
        blocks: [
            tab('increases energy', '', '<p>Your body will no longer use up all its energy battling the fungus, leaving you drained throughout the day.</p>'),
            tab('balances hormones', '', '<p>Helps bring you into a state of balance.</p>'),
            tab('removes brain fog', '', '<p>Boost your cognitive abilities and attain clear mental focus.</p>'),
            tab('improved recovery', '', '<p>Easily recover from workouts and everyday stress.</p>'),
            tab('lower cortisol levels', '', '<p>Eliminate excess cortisol and enjoy improved sleep.</p>'),
            tab('detox', '', "<p>Supports your body's activation of detox pathways and lymphatic drainage systems.</p>"),
        ],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const contentTabsMeta = {
    label: 'Content tabs',
    group: 'Content',
    icon: 'layout',
};
//# sourceMappingURL=schema.js.map